import { AK } from '@/constants';
import { AxiosPost_Nocatch } from '@/utilities/axioscall';
import React, { useEffect, useState, useRef } from 'react';
import { AxiosResponse } from 'axios';
import { axiosSetProductReviews } from '@/utilities/axioscall';
import toast from 'react-hot-toast';
import { getAssetsBaseUrl } from '@/services/storeService';

interface Product {
  id: string;
  name: string;
  eng_name: string;
  category: string;
  subcategory: string | null;
  imageurl: string[];
  selling_price: string;
  mrp: string;
  discount: string;
  star_rating: number;
  description: string;
  color_options: string[];
  size_options: string[];
  seo_url: string;
  meta_tag_title?: string;
  meta_tag_desc?: string;
}

interface RatingBoxProps {
  product: Product;
  onSubmitRating?: (data: {
    rating: number;
    feedback: string;
    images: File[];
    timestamp: string;
  }) => void;
  // assets?: string;
}

export default function RatingBox({ product, onSubmitRating }: RatingBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [fileObjects, setFileObjects] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [ratingError, setRatingError] = useState<string>('');
  const [commentError, setCommentError] = useState<string>('');
  const [reviewLoader, setReviewLoader] = useState(false);
  const [canReview, setCanReview] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  console.log(product, 'productimage')

  const assets = `${getAssetsBaseUrl()}`;

  const storeId = process.env.NEXT_PUBLIC_STORE_ID || '';
  const storeCode = process.env.NEXT_PUBLIC_STORE_CODE || '';

  const togglePopup = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setRatingError('');
      setCommentError('');
      setError('');
      setSuccessMessage('');
      setFileObjects([]);
      setFilePreviews([]);
      setFileNames([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      setError('No file selected.');
      return;
    }

    const files = Array.from(e.target.files);
    let errorMsg = '';

    if (files.length + fileObjects.length > 4) {
      errorMsg = 'You can upload a maximum of 4 files.';
    } else {
      files.forEach((file) => {
        if (!(file instanceof File)) {
          errorMsg = 'Invalid file detected.';
        } else {
          const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'video/mp4'];
          if (!allowedTypes.includes(file.type)) {
            errorMsg = 'Only JPEG, PNG, JPG, WEBP, or MP4 files are allowed.';
          } else if (file.size > 5 * 1024 * 1024) {
            errorMsg = 'Each file size must be less than 5MB.';
          }
        }
      });
    }

    if (errorMsg) {
      setError(errorMsg);
    } else {
      setError('');
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setFileObjects((prev) => [...prev, ...files]);
      setFilePreviews((prev) => [...prev, ...newPreviews]);
      setFileNames((prev) => [...prev, ...files.map((file) => file.name)]);
      console.log('Selected files:', files.map((file) => ({ name: file.name, type: file.type, size: file.size })));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    setFileObjects((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => {
      const url = prev[index];
      URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== index);
    });
    setFileNames((prev) => prev.filter((_, i) => i !== index));
    console.log('File removed at index:', index);
  };

  const getErrorMessage = () => {
    const errors = [ratingError, commentError, error].filter(Boolean);
    return errors.length > 0 ? errors.join(' ') : '';
  };

  const notifyreview = () =>
    toast.success('Review added successfully.', {
      style: { border: '1px solid #00b852', padding: '16px', color: '#00b852' },
      iconTheme: { primary: '#00b852', secondary: '#FFFAEE' },
    });

  const notifyerror = (msg: string) =>
    toast.error(msg, {
      style: { border: '1px solid #cc0000', padding: '16px', color: '#cc0000' },
      iconTheme: { primary: '#cc0000', secondary: '#FFFAEE' },
    });

  const handleAddReview = async () => {
    setError('');
    setRatingError('');
    setCommentError('');
    setSuccessMessage('');

    if (rating === 0) {
      setRatingError('Please give a star rating.');
      return;
    }

    if (feedback.trim().length < 10) {
      setCommentError('Please provide at least 10 characters in your feedback.');
      return;
    }
    if (!/^[a-zA-Z0-9\s.,!?]{10,}$/.test(feedback.trim())) {
      setCommentError('Feedback must contain meaningful text (at least 10 characters, including letters or numbers).');
      return;
    }

    if (!storeId || !storeCode) {
      setError('Store information is missing. Please try again.');
      return;
    }

    setReviewLoader(true);

    try {
      const formData = new FormData();
      formData.append('rating', rating.toString());
      formData.append('storeid', storeId);
      formData.append('storecode', storeCode);
      formData.append('productid', product.id);
      formData.append('comment', feedback);

      fileObjects.forEach((file, index) => {
        console.log('Appending file:', file.name, file.type, file.size);
        formData.append('files[]', file);
      });

      console.log('FormData contents:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof File ? `${value.name} (${value.type}, ${value.size} bytes)` : value}`);
      }

      const response = await axiosSetProductReviews(
        product.id,
        rating,
        feedback,
        fileObjects
      );

      if (response?.data?.success) {
        notifyreview();
        setSuccessMessage('Review submitted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        if (typeof onSubmitRating === 'function') {
          const data = {
            rating,
            feedback,
            images: fileObjects,
            timestamp: new Date().toISOString(),
          };
          await onSubmitRating(data);
        }
        setRating(0);
        setFeedback('');
        setFileObjects([]);
        setFilePreviews((prev) => {
          prev.forEach((url) => URL.revokeObjectURL(url));
          return [];
        });
        setFileNames([]);
        setError('');
        setRatingError('');
        setCommentError('');
        setIsOpen(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        let errorMessage =
          response?.response?.data?.message || 'Failed to submit review. Please try again.';

        console.log('errorMessage', response.response);

        if (
          response?.response?.data?.errors &&
          typeof response?.response.data.errors === 'object'
        ) {
          errorMessage = Object.values(response?.response.data.errors)
            .flat()
            .join(' ');
        }

        notifyerror(errorMessage);
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('Error submitting review:', err);

      let errorMessage =
        err.response?.data?.message || 'Failed to submit review. Please try again.';

      if (
        err.response?.data?.errors &&
        typeof err.response?.data.errors === 'object'
      ) {
        errorMessage = Object.values(err.response?.data.errors)
          .flat()
          .join(' ');
      }

      notifyerror(errorMessage);
      setError(errorMessage);
    } finally {
      setReviewLoader(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      filePreviews.forEach((url) => URL.revokeObjectURL(url));
      setFilePreviews([]);
    }
    return () => {
      document.body.style.overflow = '';
      filePreviews.forEach((url) => URL.revokeObjectURL(url));
      setFilePreviews([]);
    };
  }, [isOpen]);


  const rawImages = product.imageurl?.[0] || "";

  const firstImage = rawImages
    .split(",")                    // split by comma
    .map(img => img.trim())        // remove empty spaces
    .map(img => img.replace(/^.*\/uploads/, "uploads")) // remove everything before /uploads
    .find(img => img.startsWith("uploads")); // get first valid link


  const imageToShow = firstImage ? `${assets}${firstImage}` : "/ecomsaas-assets/images/no-image.webp";


  return (
    <section className="container-y relative block">
      <div className="container mx-auto">
        <div className="producMainWrpr">
          {canReview && (
            <a
              href="#"
              className="text-rating_text border-2 text-[12px] font-bold px-2 py-1 rounded"
              onClick={(e) => {
                e.preventDefault();
                togglePopup();
              }}
            >
              RATE
            </a>
          )}

          {isOpen && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              onClick={togglePopup}
            >
              <div
                className="bg-white rounded-[8px] p-6 max-w-[500px] mx-[16px] w-full max-h-[90vh] overflow-auto relative md:p-8"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-gray_text hover:text-gray_text text-2xl"
                  onClick={togglePopup}
                >
                  ×
                </button>

                <div className="space-y-5">
                  <div className="flex gap-2">
                    <div>
                      {product.imageurl?.length > 0 && (
                        <img
                          src={imageToShow}
                          alt={product.name}
                          className="w-[70px] rounded-[4px] image-cover"
                        />
                      )}
                    </div>

                    <div>
                      <h2 className="text-[16px] text-start font-bold">{product.name}</h2>
                      <p className="text-gray_text text-start text-[12px] font-semibold">{product.eng_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              key={star}
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`w-5 h-5 cursor-pointer transition-all duration-300 lucide lucide-star-icon lucide-star ${rating >= star ? 'fill-yellow-400 stroke-yellow-400' : 'fill-none stroke-gray_text'
                                }`}
                              onClick={() => setRating(star)}
                            >
                              <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
                            </svg>
                          ))}
                        </div>
                        {rating > 0 && <span className="text-sm text-gray-600 font-medium">{rating} / 5</span>}
                      </div>
                    </div>
                  </div>

                  <div>
                    <textarea
                      className="w-full h-[140px] text-gray_text shadow-inner rounded-[8px] p-2 border focus:outline-none"
                      placeholder="Enter your text here..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                  </div>

                  {filePreviews.length > 0 && (
                    <div className="flex gap-4 mt-4 w-full overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
                      {filePreviews.map((preview, index) => (
                        <div key={index} className="relative border-solid border-1 border-black flex-shrink-0" style={{ width: 'calc(100% / 4 - 0.5rem)' }}>
                          <div className="relative border-solid border-1 border-black">
                            <img
                              src={preview}
                              alt={`Uploaded ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-md border"
                            />
                            <button
                              onClick={() => handleRemove(index)}
                              className="absolute top-0 right-0 bg-gray_text text-white rounded-full h-5 w-5 flex items-center justify-center transition"
                            >
                              ×
                            </button>
                            <p className="text-xs text-gray-700 mt-1 text-center truncate">{fileNames[index]}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-col items-center">
                    <label className="w-full border-2 border-dashed border-primary p-4 rounded-md cursor-pointer hover:bg-gray-50 text-center">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/webp,video/mp4"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        ref={fileInputRef}
                      />
                      <p className="text-gray-600 text-sm">Attach your files here (JPEG/PNG/JPG/WEBP/MP4, max 4 files)</p>
                    </label>

                    {successMessage && <p className="text-success text-sm mt-2">{successMessage}</p>}
                    {getErrorMessage() && <p className="text-error text-sm mt-2">{getErrorMessage()}</p>}

                    <div className="w-full mt-4">
                      <button
                        className={`text-white !bg-primary w-full py-2 rounded-[4px] hover:rating_light ${reviewLoader ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        onClick={handleAddReview}
                        disabled={reviewLoader}
                      >
                        {reviewLoader ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}